import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

/**
 * Las lecturas de Atledi se escriben, no aparecen: 16 caracteres cada 55 ms
 * desde que el bloque entra en pantalla. Si el texto cambia (otra selección,
 * otro evento) se vuelve a escribir desde el principio.
 *
 *   const { ref, text, typing } = useTypewriter(fullText);
 */
export function useTypewriter(full: string, opts: { step?: number; intervalMs?: number } = {}) {
  const step = opts.step ?? 16;
  const interval = opts.intervalMs ?? 55;
  const [n, setN] = useState(0);
  const [live, setLive] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const timer = useRef<number | null>(null);

  // Texto nuevo: se reinicia; si ya está a la vista, empieza a escribir ya.
  useEffect(() => {
    setN(0);
  }, [full]);

  useEffect(() => {
    if (!live) return;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setN(full.length);
      return;
    }
    if (n >= full.length) return;
    timer.current = window.setTimeout(() => setN((x) => Math.min(full.length, x + step)), interval);
    return () => {
      if (timer.current != null) window.clearTimeout(timer.current);
    };
  }, [live, n, full, step, interval]);

  const ref = (el: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el || live) return;
    if (typeof IntersectionObserver === 'undefined') {
      setLive(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          setLive(true);
        }
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    observer.current = io;
  };

  // Al imprimir, el texto entero: nadie espera a que se escriba en el PDF.
  useEffect(() => {
    const finish = () => {
      observer.current?.disconnect();
      // Síncrono: el navegador toma la foto en cuanto vuelve `beforeprint`.
      flushSync(() => {
        setLive(true);
        setN(full.length);
      });
    };
    window.addEventListener('beforeprint', finish);
    const mq = window.matchMedia?.('print');
    const onMq = (e: MediaQueryListEvent) => { if (e.matches) finish(); };
    mq?.addEventListener?.('change', onMq);
    return () => {
      window.removeEventListener('beforeprint', finish);
      mq?.removeEventListener?.('change', onMq);
      observer.current?.disconnect();
    };
  }, [full]);

  return { ref, text: full.slice(0, n), typing: live && n < full.length };
}
